import { Minus, Plus } from 'lucide-react';
import { useId, useState, type ComponentPropsWithRef, type ReactNode } from 'react';
import { describedByOf, Field, type FieldTexts } from '../Field/Field.tsx';
import fieldStyles from '../Input/Input.module.css';
import styles from './NumberStepper.module.css';

export type NumberStepperProps = Omit<
  ComponentPropsWithRef<'input'>,
  'type' | 'value' | 'defaultValue' | 'onChange' | 'min' | 'max' | 'step'
> &
  FieldTexts & {
    value: number;
    min: number;
    max: number;
    decrementLabel: string;
    incrementLabel: string;
    onValueChange: (value: number) => void;
  };

const WHOLE_NUMBER = /^\d+$/u;

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const StepButton = ({
  label,
  icon,
  disabled,
  onStep,
}: {
  label: string;
  icon: ReactNode;
  disabled: boolean;
  onStep: () => void;
}) => (
  <button
    type="button"
    tabIndex={-1}
    className={styles['step']}
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onStep}
  >
    {icon}
  </button>
);

export const NumberStepper = ({
  label,
  helperText,
  errorText,
  hideLabel,
  required,
  value,
  min,
  max,
  decrementLabel,
  incrementLabel,
  onValueChange,
  onBlur,
  disabled = false,
  id,
  className,
  ...rest
}: NumberStepperProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [draft, setDraft] = useState<string | null>(null);
  const commit = (next: number) => {
    setDraft(null);
    onValueChange(clamp(next, min, max));
  };
  const accept = (text: string) => {
    setDraft(text);
    const typed = Number(text);
    if (WHOLE_NUMBER.test(text) && typed >= min && typed <= max) onValueChange(typed);
  };
  return (
    <Field
      id={inputId}
      label={label}
      helperText={helperText}
      errorText={errorText}
      hideLabel={hideLabel}
      required={required}
    >
      <span
        className={[fieldStyles['affixed'], styles['stepper'], errorText ? fieldStyles['invalid'] : ''].join(
          ' ',
        )}
      >
        <StepButton
          label={decrementLabel}
          icon={<Minus size={16} aria-hidden="true" />}
          disabled={disabled || value <= min}
          onStep={() => {
            commit(value - 1);
          }}
        />
        <input
          id={inputId}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          step={1}
          value={draft ?? String(value)}
          disabled={disabled}
          required={required}
          className={[fieldStyles['affixedInput'], styles['input'], className ?? ''].join(' ')}
          aria-invalid={errorText ? true : undefined}
          aria-describedby={describedByOf(inputId, { helperText, errorText })}
          onChange={(event) => {
            accept(event.target.value);
          }}
          onBlur={(event) => {
            setDraft(null);
            onBlur?.(event);
          }}
          {...rest}
        />
        <StepButton
          label={incrementLabel}
          icon={<Plus size={16} aria-hidden="true" />}
          disabled={disabled || value >= max}
          onStep={() => {
            commit(value + 1);
          }}
        />
      </span>
    </Field>
  );
};
